import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';

export default function EarlyBirdPopup() {
  const { user, setUser, getAuthHeaders, sessionToken } = useAuth();
  const [visible, setVisible] = useState(false);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const popupKey = `advoq_community_popup_${sessionToken || user?.user_id || 'guest'}`;

  useEffect(() => {
    if (!user) return;
    const isFree = String(user.plan || 'FREE').toUpperCase() === 'FREE';
    const hasAccess = Boolean(user.jolly_early_access);
    const dismissed = sessionStorage.getItem(popupKey) === 'dismissed';
    if (isFree && !hasAccess && !dismissed) {
      const t = setTimeout(() => setVisible(true), 1200);
      return () => clearTimeout(t);
    }
  }, [popupKey, user]);

  const dismiss = () => {
    sessionStorage.setItem(popupKey, 'dismissed');
    setVisible(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/redeem-community-code`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ code: code.trim() }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        toast.error(data.detail || 'Invalid code. Please try again.');
        return;
      }
      const data = await res.json();
      if (setUser && data.user) setUser(data.user);
      toast.success('Community-tier access unlocked.');
      dismiss();
      window.location.reload();
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!visible) return null;

  return (
    <div style={{
      position: 'fixed',
      bottom: '24px',
      right: '24px',
      zIndex: 8888,
      width: '340px',
      background: 'var(--bg-surface)',
      border: '1px solid var(--border-default)',
      borderRadius: '16px',
      padding: '20px',
      boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
      animation: 'slideInUp 0.3s ease',
    }}>
      <style>{`
        @keyframes slideInUp {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      <button
        type="button"
        onClick={dismiss}
        style={{
          position: 'absolute', top: '12px', right: '12px',
          background: 'none', border: 'none', cursor: 'pointer',
          color: 'var(--text-muted)', padding: '4px',
        }}
      >
        <X size={15} />
      </button>

      <p style={{ margin: '0 0 4px', fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)' }}>
        Have a community access code?
      </p>
      <p style={{ margin: '0 0 14px', fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
        Enter it below to unlock an approved community-tier program.{' '}
        <a href="mailto:support@advoq.in" style={{ color: 'var(--accent-primary)', fontWeight: 600 }}>
          Email support@advoq.in
        </a>
        {' '}if you need one.
      </p>

      <form onSubmit={handleSubmit} style={{ display: 'flex', gap: '8px' }}>
        <input
          className="advoq-input"
          value={code}
          onChange={e => setCode(e.target.value)}
          placeholder="Access code"
          style={{ flex: 1, fontSize: '13px' }}
          autoComplete="off"
        />
        <button
          type="submit"
          className="advoq-btn-primary"
          disabled={loading || !code.trim()}
          style={{ whiteSpace: 'nowrap', padding: '0 14px', fontSize: '13px' }}
        >
          {loading ? '…' : 'Unlock'}
        </button>
      </form>
    </div>
  );
}
